import { useCallback } from 'react';
import * as Cesium from 'cesium';
import { ACCENT, ACCENT_FILL } from '../constants';

export function useHighlights(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  highlightDataSourceRef: React.MutableRefObject<any>,
  selectionDataSourceRef: React.MutableRefObject<any>,
  gsImagePropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  gsConnectionTypeRef: React.MutableRefObject<Map<string, 'bidirectional' | 'unidirectional'>>,
  gsActiveMap: React.MutableRefObject<Record<string, boolean>>,
  geoPointImagePropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  geoOutlineColorPropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  geoFillColorPropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  satelliteFollowTokenRef: React.MutableRefObject<number>,
  satelliteFollowSatIdRef: React.MutableRefObject<string | null>,
  satelliteFollowDistanceRef: React.MutableRefObject<number | null>,
  satelliteFollowRemoveRef: React.MutableRefObject<Cesium.Event.RemoveCallback | null>,
  orbitLeadTimeRef: React.MutableRefObject<number>,
  orbitTrailTimeRef: React.MutableRefObject<number>,
  satOrbitActiveMap: React.MutableRefObject<Record<string, boolean>>,
  layersRef: React.MutableRefObject<any>,
) {
  const highlightSatellite = useCallback((satId: string | string[] | null) => {
    const hds = highlightDataSourceRef.current;
    const ds = sharedDataSourceRef.current;
    if (!hds) return;

    hds.entities.removeAll();

    if (!satId || !ds) return;
    const ids = Array.isArray(satId) ? satId : [satId];

    ids.forEach((id, idx) => {
      // If satellite/orbit is toggled off (hidden), do not highlight
      if (satOrbitActiveMap.current[id] === false || layersRef.current.satellites === false) {
        return;
      }

      const satEntity = ds.entities.getById(id);
      if (!satEntity) return;

      const time = viewerRef.current?.clock.currentTime ?? Cesium.JulianDate.now();
      let originalColor = Cesium.Color.WHITE;
      if (satEntity.point && satEntity.point.color) {
        originalColor = satEntity.point.color.getValue(time) || Cesium.Color.WHITE;
      }

      hds.entities.add({
        id: `sat_highlight_point_${id}_${idx}`,
        position: satEntity.position as Cesium.PositionProperty,
        point: {
          pixelSize: 14,
          color: originalColor,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2.2,
          show: true,
        },
      });

      if (satEntity.position) {
        hds.entities.add({
          id: `sat_highlight_path_${id}_${idx}`,
          position: satEntity.position as Cesium.PositionProperty,
          path: {
            material: new Cesium.PolylineOutlineMaterialProperty({
              color: originalColor,
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 1.2,
            }),
            width: 4.6,
            leadTime: new Cesium.CallbackProperty(() => orbitLeadTimeRef.current, false),
            trailTime: new Cesium.CallbackProperty(() => orbitTrailTimeRef.current, false),
            resolution: 60,
            show: true,
          },
        });
      }
    });
  }, []);

  const highlightGroundStation = useCallback((gsId: string | null) => {
    const hds = highlightDataSourceRef.current;
    const ds = sharedDataSourceRef.current;
    if (!hds) return;

    hds.entities.removeAll();

    if (!gsId || !ds) return;
    if (gsActiveMap.current[gsId] === false) return;
    const gsEntity = ds.entities.getById(`gs_point_${gsId}`);
    if (!gsEntity?.position) return;

    hds.entities.add({
      id: 'gs_hover_marker',
      position: gsEntity.position,
      billboard: {
        image: '/assets/gs_hover.svg',
        scale: 1.0,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  }, []);

  const highlightGeoFeature = useCallback((featureId: string | null) => {
    if (!featureId) {
      geoPointImagePropsRef.current.forEach((imageProp) => {
        imageProp.setValue('/assets/point_white.png');
      });
      geoOutlineColorPropsRef.current.forEach((colorProp) => {
        colorProp.setValue(Cesium.Color.WHITE);
      });
      return;
    }

    const imageProp = geoPointImagePropsRef.current.get(featureId);
    if (imageProp) {
      imageProp.setValue('/assets/point_white_hover.png');
    }

    const colorProp = geoOutlineColorPropsRef.current.get(featureId);
    if (colorProp) {
      colorProp.setValue(ACCENT);
    }
  }, []);

  const setSelectedSatellite = useCallback((satId: string | null) => {
    const sds = selectionDataSourceRef.current;
    const ds = sharedDataSourceRef.current;
    if (!sds) return;

    const prevPoint = sds.entities.getById('selected_sat_point');
    if (prevPoint) sds.entities.remove(prevPoint);
    const prevPath = sds.entities.getById('selected_sat_path');
    if (prevPath) sds.entities.remove(prevPath);

    const v = viewerRef.current;
    if (v && v.selectedEntity) {
      if (!satId || v.selectedEntity.id === satId || v.selectedEntity.id === 'selected_sat_point' || v.selectedEntity.id === 'selected_sat_path') {
        console.log('[highlights] Clearing native selectedEntity:', v.selectedEntity.id);
        v.selectedEntity = undefined;
      }
    }

    if (!satId || !ds) return;
    // If satellite/orbit is toggled off (hidden), do not highlight selection
    if (satOrbitActiveMap.current[satId] === false || layersRef.current.satellites === false) {
      return;
    }

    const satEntity = ds.entities.getById(satId);
    if (!satEntity) return;

    const time = viewerRef.current?.clock.currentTime ?? Cesium.JulianDate.now();
    let originalColor = Cesium.Color.WHITE;
    if (satEntity.point && satEntity.point.color) {
      originalColor = satEntity.point.color.getValue(time) || Cesium.Color.WHITE;
    }

    sds.entities.add({
      id: 'selected_sat_point',
      position: satEntity.position as Cesium.PositionProperty,
      point: {
        pixelSize: 14,
        color: originalColor,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2.2,
        show: true,
      },
    });

    if (satEntity.position) {
      sds.entities.add({
        id: 'selected_sat_path',
        position: satEntity.position as Cesium.PositionProperty,
        path: {
          material: new Cesium.PolylineOutlineMaterialProperty({
            color: originalColor,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 1.2,
          }),
          width: 4.6,
          leadTime: new Cesium.CallbackProperty(() => orbitLeadTimeRef.current, false),
          trailTime: new Cesium.CallbackProperty(() => orbitTrailTimeRef.current, false),
          resolution: 60,
          show: true,
        },
      });
    }
  }, []);

  const setSelectedGroundStations = useCallback((gsIds: string[]) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    const sds = selectionDataSourceRef.current;
    if (!v || !ds || !sds) return;

    const existing = sds.entities.values
      .filter(e => String(e.id).startsWith('selected_gs_'))
      .map(e => e.id);

    existing.forEach(id => {
      const gsId = String(id).replace('selected_gs_', '');
      if (!gsIds.includes(gsId)) {
        const imageProp = gsImagePropsRef.current.get(gsId);
        if (imageProp) {
          if (gsActiveMap.current[`${gsId}_connected`]) {
            const connectionType = gsConnectionTypeRef.current.get(gsId) ?? 'bidirectional';
            imageProp.setValue(connectionType === 'bidirectional' ? '/assets/connection_bidirectional.svg' : '/assets/connection_unidirectional.svg');
          } else {
            imageProp.setValue('/assets/gs_enabled.svg');
          }
        }
      }
      const entity = sds.entities.getById(String(id));
      if (entity) sds.entities.remove(entity);
    });

    gsIds.forEach((gsId) => {
      const gsEntity = ds.entities.getById(`gs_point_${gsId}`);
      if (!gsEntity?.position) return;
      const pos = (gsEntity.position as Cesium.PositionProperty).getValue(v.clock.currentTime);
      if (!pos) return;

      const imageProp = gsImagePropsRef.current.get(gsId);
      if (imageProp) {
        imageProp.setValue('/assets/select_gs.svg');
      }
    });
  }, []);

  const setSelectedGeoFeatures = useCallback((featureIds: string[]) => {
    const ds = sharedDataSourceRef.current;
    const sds = selectionDataSourceRef.current;
    if (!ds || !sds) return;

    const existing = sds.entities.values
      .filter(e => String(e.id).startsWith('selected_geo_'))
      .map(e => e.id);
    existing.forEach(id => {
      const featureId = String(id).replace('selected_geo_', '').replace('_outline', '');
      const fillColorProp = geoFillColorPropsRef.current.get(featureId);
      if (fillColorProp) {
        fillColorProp.setValue(Cesium.Color.WHITE.withAlpha(0.1));
      }
      const entity = sds.entities.getById(String(id));
      if (entity) sds.entities.remove(entity);
    });

    featureIds.forEach((featureId) => {
      const entity = ds.entities.getById(featureId);
      if (!entity) return;

      const fillColorProp = geoFillColorPropsRef.current.get(featureId);
      if (fillColorProp) {
        fillColorProp.setValue(ACCENT_FILL);
      }

      if (entity.position) {
        sds.entities.add({
          id: `selected_geo_${featureId}`,
          position: entity.position,
          billboard: {
            image: '/assets/point_green.png',
            scale: 1.0,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        return;
      }

      const outline = ds.entities.getById(`${featureId}_outline`);
      const positions = outline?.polyline?.positions;
      if (positions) {
        sds.entities.add({
          id: `selected_geo_${featureId}_outline`,
          polyline: {
            positions,
            width: 3,
            material: new Cesium.PolylineGlowMaterialProperty({
              glowPower: 0.25,
              color: ACCENT.withAlpha(0.95),
            }),
            clampToGround: true,
            show: true,
            arcType: Cesium.ArcType.GEODESIC,
          },
        });
      }
    });
  }, []);

  const stopFollowingSatellite = useCallback(() => {
    satelliteFollowTokenRef.current += 1;
    satelliteFollowSatIdRef.current = null;
    satelliteFollowDistanceRef.current = null;
    if (satelliteFollowRemoveRef.current) {
      satelliteFollowRemoveRef.current();
      satelliteFollowRemoveRef.current = null;
    }
  }, []);

  return {
    highlightSatellite,
    highlightGroundStation,
    highlightGeoFeature,
    setSelectedSatellite,
    setSelectedGroundStations,
    setSelectedGeoFeatures,
    stopFollowingSatellite,
  };
}
